import { useCallback } from "react";
import { UserDto } from "../types/user.types";
import { useAuthenticatedRequestService, useRequestService } from "./service.base";

export function useUserService() {
  const USERS_PATH = "users";
  const { authGet, authPost, authDelete } = useAuthenticatedRequestService();
  const { httpPost } = useRequestService();

  const invite = useCallback(
    async function (email: string, roleId: string): Promise<UserDto> {
      return await authPost(USERS_PATH + "/invite", { email, roleId });
    },
    [authPost]
  );

  const register = useCallback(
    async function (fullname: string, shortname: string, password: string, token: string) {
      return await httpPost(USERS_PATH + "/validate", { fullname, shortname, password, token });
    },
    [httpPost]
  );

  const requestPasswordReset = useCallback(
    async function (email: string): Promise<null> {
      return await httpPost(USERS_PATH + "/sendresetpassword", { email });
    },
    [httpPost]
  );

  const resetPassword = useCallback(
    async function (password: string, token: string): Promise<null> {
      return await authPost(USERS_PATH + "/resetpassword", { password, token });
    },
    [authPost]
  );

  const getMembers = useCallback(
    async function (): Promise<UserDto[]> {
      return await authGet(USERS_PATH + "/members");
    },
    [authGet]
  );

  const getReviewers = useCallback(
    async function (): Promise<UserDto[]> {
      return await authGet(USERS_PATH + "/reviewers");
    },
    [authGet]
  );

  const deleteUser = useCallback(
    async function (id: string): Promise<UserDto[]> {
      return await authDelete(`${USERS_PATH}/${id}`);
    },
    [authDelete]
  );

  const getLockStatus = useCallback(
    async function (userId: number): Promise<{ userId: number; isLocked: boolean }> {
      return await authGet(`${USERS_PATH}/${userId}/lockout`);
    },
    [authGet]
  );

  const lockUser = useCallback(
    async function (userId: number): Promise<{ message: string; userId: number; isLocked: boolean }> {
      return await authPost(`${USERS_PATH}/${userId}/lockout`, {});
    },
    [authPost]
  );

  const unlockUser = useCallback(
    async function (userId: number): Promise<{ message: string; userId: number; isLocked: boolean }> {
      return await authDelete(`${USERS_PATH}/${userId}/lockout`);
    },
    [authDelete]
  );

  const funcs = {
    invite,
    register,
    requestPasswordReset,
    resetPassword,
    getMembers,
    getReviewers,
    deleteUser,
    getLockStatus,
    lockUser,
    unlockUser,
  };
  return funcs;
}
